import * as React from 'react';
import partition from 'lodash/partition';
import { useForm } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { useTranslator } from '@gandi/translate';
import { Base, Block, BlockContent, BlockButtons, Button, Help, Tooltip, TooltipContainer } from '@gandi/ui';
import { useReadOnly } from '../../../../formUtils/readOnly';
import { useFormInitialValues } from '../../../../formUtils/useFormInitialValues';
import { useIsValidSection } from '../../../../formUtils/useIsValidSection';
import SectionHeader from '../SectionHeader';
import WebForwardForm from './components/WebForwardForm';
import WebForwardList from './components/WebForwardList';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const sectionName = 'webForwards';
const createWebForward = () => ({
  isNew: true,
  sourceProtocol: 'https',
  host: '',
  targetProtocol: 'https://',
  targetUrl: '',
  type: 'http301'
});
const WebForwardsSection = ({
  onDelete,
  disabled,
  ...blockProps
}) => {
  const {
    __
  } = useTranslator();
  const initialValues = useFormInitialValues();
  const {
    fields
  } = useFieldArray(sectionName, {});
  const {
    change
  } = useForm();
  const fieldsAndValues = fields.map((prefix, index) => ({
    index,
    prefix,
    forward: fields.value[index]
  }));
  const [webForwards, newWebForwards] = partition(fieldsAndValues, field => !field.forward.isNew);
  const {
    readOnly,
    setReadOnly,
    toggleReadOnly
  } = useReadOnly(sectionName);
  const isValidSection = useIsValidSection(sectionName);
  React.useEffect(() => {
    // $FlowFixMe[prop-missing]: prop exists but not typed
    if (fields.length === 0) {
      fields.push(createWebForward());
    }
    if (webForwards.length === 0) {
      // Not read only on section creation
      // In case values change, this readonly state must be handled by the
      // controller to prevent unexpected results, e.g. readOnly false without
      // the section
      setReadOnly(sectionName, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleRemoveWebForward = forwardField => {
    // $FlowFixMe[prop-missing]: prop exists but not typed
    // $FlowFixMe[incompatible-type]
    const fdCount = fields.length;
    fields.remove(forwardField.index);
    // the length in fields is updated until the component has been re-rendered
    // So we create a new forward when we know this is the last we are removing
    // XXX: We could use a redux selector instead that should be more reliable
    if (fdCount === 1) {
      fields.push(createWebForward());
    }
  };
  const handleAddNewWebForwarding = () => {
    fields.push(createWebForward());
    setReadOnly(sectionName, false);
  };
  const handleClickCancel = () => {
    change(sectionName, initialValues?.webForwards);
    setReadOnly(sectionName, true);
    if (!initialValues?.webForwards) {
      onDelete();
    }
  };
  const handleClickDone = () => {
    // TODO: validate section w/ dry-run?

    newWebForwards.forEach(forwardField => {
      // $FlowFixMe[prop-missing] exists but not declared
      fields.update(forwardField.index, {
        ...forwardField.forward,
        isNew: false
      });
    });
    setReadOnly(sectionName, true);
  };
  const handleDeleteSection = () => {
    change(sectionName, null);
    onDelete();
    setReadOnly(sectionName, true);
  };
  return /*#__PURE__*/_jsxs(Block, {
    ...blockProps,
    children: [/*#__PURE__*/_jsx(SectionHeader, {
      title: __('Web forwarding'),
      onEditToggle: () => toggleReadOnly(sectionName),
      readOnlyMode: readOnly,
      disabled: disabled
    }), /*#__PURE__*/_jsxs(BlockContent, {
      children: [/*#__PURE__*/_jsxs(Base, {
        marginBottom: readOnly || webForwards.length > 0 && newWebForwards.length > 0 ? 'small' : 'none',
        children: [/*#__PURE__*/_jsx(Help, {
          children: /*#__PURE__*/_jsx("span", {
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML: __('The web forwarding you are currently configuring will not erase ' + 'any currently existing web forwarding addresses used.<br/>' + 'To delete existing web forwarding adresses, you have to do it ' + "manually from your domain's web forwarding administration page.", {
              withHTML: true
            })
          })
        }), webForwards.length > 0 && /*#__PURE__*/_jsx(Base, {
          marginTop: "base",
          children: /*#__PURE__*/_jsx(WebForwardList, {
            forwardFields: webForwards,
            readOnly: readOnly,
            onDeleteItem: handleRemoveWebForward
          })
        })]
      }), newWebForwards.map((forwardField, index) => /*#__PURE__*/_jsx(Base, {
        separatorTop: index > 0 || webForwards.length > 0,
        paddingTop: index > 0 || webForwards.length > 0 ? 'xsmall' : 'small',
        paddingBottom: index === newWebForwards.length - 1 ? undefined : 'xsmall',
        children: /*#__PURE__*/_jsx(WebForwardForm, {
          forwardField: forwardField,
          onDeleteWebForward: handleRemoveWebForward,
          removable: webForwards.length > 0 || newWebForwards.length > 0 && index > 0
        })
      }, forwardField.prefix))]
    }), !readOnly && /*#__PURE__*/_jsxs(BlockButtons, {
      children: [/*#__PURE__*/_jsx(Button, {
        type: "button",
        kind: "outline",
        small: true,
        onClick: handleClickCancel,
        children: __('Cancel')
      }), /*#__PURE__*/_jsx(TooltipContainer, {
        id: "section-webforward-done-tooltip",
        enabled: !isValidSection,
        renderReference: getReferenceProps => /*#__PURE__*/_jsx(Button, {
          ...getReferenceProps(),
          type: "button",
          small: true,
          onClick: handleClickDone,
          disabled: !isValidSection,
          children: __('Done')
        }),
        children: getTooltipProps => /*#__PURE__*/_jsx(Tooltip, {
          ...getTooltipProps(),
          children: __('You need to complete this section')
        })
      }), /*#__PURE__*/_jsxs(Button, {
        type: "button",
        kind: "link",
        onClick: handleAddNewWebForwarding,
        children: [/*#__PURE__*/_jsx("span", {
          "aria-hidden": true,
          children: "+ "
        }), __('Add forwarding')]
      }), /*#__PURE__*/_jsx(Button, {
        type: "button",
        kind: "ghost",
        onClick: handleDeleteSection,
        small: true,
        children: __('Delete section')
      })]
    })]
  });
};
export default WebForwardsSection;